import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';

const highlights = [
  {
    icon: 'ri-shield-check-line',
    title: '安全可靠',
    description: '采用SSL/TLS加密传输，多重身份验证，全方位保障您的账号和数据安全。',
  },
  {
    icon: 'ri-speed-up-line',
    title: '极速访问',
    description: '全球多节点部署，CDN加速技术，确保毫秒级响应速度，畅享流畅体验。',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '贴心服务',
    description: '7×24小时在线客服，专业团队随时为您解答疑问，解决使用难题。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '多端兼容',
    description: 'PC端与移动端完美适配，数据实时同步，随时随地畅享平台服务。',
  },
];

const advantages = [
  {
    icon: 'ri-user-star-line',
    title: '百万用户信赖',
    description: '累计服务超过百万用户，好评率高达98.5%，是您值得信赖的平台选择。',
  },
  {
    icon: 'ri-time-line',
    title: '稳定运行保障',
    description: '平台稳定运行超1500天，全年可用率达99.99%，确保服务永不间断。',
  },
  {
    icon: 'ri-lock-password-line',
    title: '数据安全保障',
    description: '通过国际安全认证，采用银行级加密技术，保护您的每一份数据。',
  },
  {
    icon: 'ri-rocket-2-line',
    title: '持续创新升级',
    description: '技术团队持续迭代优化，每月推出新功能，不断提升使用体验。',
  },
];

export default function HomePage() {
  const latestArticles = articles.slice(0, 6);

  const homeSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '8866h.cc',
      url: 'https://8866h.cc',
      description: '8866h.cc是您信赖的官方平台入口，提供安全稳定的访问体验。',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'support@8866h.cc',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '8866h.cc',
      url: 'https://8866h.cc',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://8866h.cc/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '8866h.cc官方入口-安全稳定快速访问',
      description: '8866h.cc是您信赖的官方平台入口，提供安全稳定的访问体验。本页为您详细介绍8866h.cc的核心功能、使用指南及常见问题解答。',
      url: 'https://8866h.cc',
      isPartOf: {
        '@type': 'WebSite',
        name: '8866h.cc',
        url: 'https://8866h.cc',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <>
      <SeoHead
        title="8866h.cc官方入口-安全稳定快速访问 | 8866h官网"
        description="8866h.cc是您信赖的官方平台入口，提供安全稳定的访问体验。本页为您详细介绍8866h.cc的核心功能、使用指南及常见问题解答，助您快速找到正确入口，畅享极速流畅的平台服务体验。"
        keywords="8866h.cc,8866h官网,8866h入口,8866h.cc官方地址,8866h平台,8866h最新地址,8866h注册登录"
        canonicalUrl="https://8866h.cc"
        ogImage="https://readdy.ai/api/search-image?query=Modern%20minimalist%20platform%20hero%20with%20warm%20golden%20accents%2C%20abstract%20geometric%20shapes%2C%20clean%20cream%20background%2C%20professional%20technology%20brand%20visual%2C%20elegant%20composition%20with%20ample%20whitespace&width=1200&height=630&seq=og-home&orientation=landscape"
        schemaData={homeSchema}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20geometric%20gradient%20background%20with%20soft%20warm%20golden%20and%20cream%20tones%2C%20flowing%20organic%20shapes%2C%20clean%20modern%20design%2C%20luxurious%20subtle%20texture%2C%20high-end%20technology%20brand%20atmosphere%2C%20elegant%20spacious%20composition%20with%20gentle%20light%20diffusion&width=1920&height=1080&seq=hero-home-bg&orientation=landscape"
              alt="8866h.cc 平台背景"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-background-50" />
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center pt-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/80 backdrop-blur-sm border border-background-200/50 mb-8">
              <i className="ri-verified-badge-line text-primary-500 text-sm"></i>
              <span className="text-sm text-foreground-700 font-medium">8866h.cc 官方认证入口</span>
            </div>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mb-6 leading-tight font-heading">
              安全稳定，极速畅享
              <br />
              <span className="text-primary-500">8866h.cc</span> 官方平台
            </h1>

            <p className="text-base md:text-lg text-foreground-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              8866h.cc是您信赖的官方平台入口，提供安全稳定的访问体验。无论新用户还是老朋友，这里都是您最正确的选择。
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/guide"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                立即开始使用
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3.5 bg-background-50/80 backdrop-blur-sm text-foreground-950 font-medium rounded-lg border border-background-300/60 hover:bg-background-100 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Highlights Section */}
        <section className="py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                为什么选择 8866h.cc？
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                四大核心优势，为您打造极致的使用体验，成为百万用户的首选平台
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-500 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-600 group-hover:text-background-50 transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                探索平台内容
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                按分类浏览，快速找到您需要的信息和服务
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group block p-8 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center mb-5 group-hover:bg-primary-500 transition-colors duration-300">
                    <i className={`${cat.icon} text-2xl text-primary-600 group-hover:text-background-50 transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-5">{cat.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{cat.articleCount} 篇文章</span>
                    <span className="text-sm text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      浏览全部 <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">最新文章</h2>
                <p className="text-foreground-500 mt-2">了解平台最新动态与使用技巧</p>
              </div>
              <Link
                to="/category/news"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/news"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                平台实力，用数据说话
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                数字背后是我们对品质的坚持与对用户的承诺
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((item, index) => (
                <div key={index} className="text-center p-6">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                常见问题解答
              </h2>
              <p className="text-foreground-500">
                快速了解8866h.cc平台，解答您最关心的问题
              </p>
            </div>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <details key={index} className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors list-none">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{item.question}</span>
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-100 flex items-center justify-center group-open:rotate-45 transition-transform">
                      <i className="ri-add-line text-foreground-500 text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
              准备好开始了吗？
            </h2>
            <p className="text-foreground-500 mb-8 max-w-xl mx-auto">
              立即访问8866h.cc官方平台，体验安全稳定的极速服务。百万用户的选择，期待您的加入！
            </p>
            <Link
              to="/category/guide"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              立即开始 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}